import React, { useState } from "react";
import moment from "moment";
import { Box } from "@mui/material";

import { ActionButton, CardLayout, InputField, SelectField } from "../v3";
import { AdminService, CRMService } from "../../services";
import { crmReportValidationRules } from "../../pages/report/validation";

export const CrmReportForm = ({ loading = false, setLoading = () => {}, onGenerate }) => {
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [errors, setErrors] = useState({});
  const [reportDetails, setReportDetails] = useState({
    assigneeId: null,
    statusId: null,
    fromDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });

  const handleAssigneeChange = (selected) => {
    setSelectedAssignee(selected);
    setReportDetails((prev) => ({ ...prev, assigneeId: selected?.id || null }));
  };

  const handleStatusChange = (selected) => {
    setSelectedStatus(selected);
    setReportDetails((prev) => ({ ...prev, statusId: selected?.id || null }));
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setReportDetails((prev) => ({ ...prev, [name]: value }));
  };

  // Utility: Generate random integer between min & max
  const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  // Utility: Get random date between two dates
  const randomDate = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diff = endDate.getTime() - startDate.getTime();
    const newDate = new Date(startDate.getTime() + Math.random() * diff);
    return newDate.toISOString().split("T")[0];
  };

  const handleGenerateReport = () => {
    setLoading(true);
    setErrors({});

    const validationErrors = crmReportValidationRules(reportDetails);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    try {
      // Example lists for mock generation
      const assignees = ["Alice Smith", "Bob Brown", "Charlie Adams"];
      const statuses = ["New", "Contacted", "In Progress", "Closed Won"];

      const recordCount = randomInt(5, 10);
      const mockData = Array.from({ length: recordCount }).map((_, index) => {
        const count = randomInt(1, 20);
        const percentage = (Math.random() * 100).toFixed(1) + "%";

        return {
          id: index + 1,
          date: randomDate(reportDetails.fromDate, reportDetails.toDate),
          assignee: selectedAssignee?.label || assignees[randomInt(0, assignees.length - 1)],
          status: selectedStatus?.label || statuses[randomInt(0, statuses.length - 1)],
          count,
          percentage,
        };
      });

      // Simulate API delay to show loader
      setTimeout(() => {
        onGenerate?.(mockData);
        setLoading(false);
      }, 1500); // 1.5 seconds
    } catch (error) {
      console.log("🚀 ~ handleGenerateReport ~ error:", error);
      setLoading(false);
    }
  };

  return (
    <CardLayout>
      <Box display="flex" gap={2}>
        <SelectField
          label="Assignee"
          value={selectedAssignee}
          getData={AdminService.getUserList}
          getDataStaticFilters={{ type: "USER" }}
          optionValueFields={["firstName", "lastName"]}
          required
          error={!!errors.assigneeId}
          helperText={errors.assigneeId}
          onChange={handleAssigneeChange}
          style={{ width: "100%" }}
        />
        <SelectField
          label="Status"
          value={selectedStatus}
          getData={CRMService.getLeadStatuses}
          required
          error={!!errors.statusId}
          helperText={errors.statusId}
          onChange={handleStatusChange}
          style={{ width: "100%" }}
        />
      </Box>
      <Box display="flex" gap={2} mt={2}>
        <InputField
          required
          type="date"
          label="From Date"
          name="fromDate"
          value={reportDetails.fromDate}
          onChange={updateDetailsState}
          error={!!errors.fromDate}
          helperText={errors.fromDate}
          fullWidth
        />
        <InputField
          required
          type="date"
          label="To Date"
          name="toDate"
          value={reportDetails.toDate}
          onChange={updateDetailsState}
          error={!!errors.toDate}
          helperText={errors.toDate}
          fullWidth
        />
      </Box>
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <ActionButton buttonText="Generate" color="primary" onButtonClick={handleGenerateReport} loading={loading} />
      </Box>
    </CardLayout>
  );
};
